/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-07-25 10:07:54
 *
 *******************************************************/
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Field, FieldArray } from 'redux-form';
import { FormLayout, SkeletonDisplayText, SkeletonBodyText, TextStyle } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';

import SearchTermTable from './common/search-term-table';
import ProductTable from './common/product-table';
import { RenderChoice2 } from 'src/components/views/common/forms/render-choice-2';
import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import {
  SEARCH_TERM_LIST,
  PRODUCT_LIST,
  NUMBER_PRODUCT_SUGGESTIONS,
  NUMBER_SEARCH_TERM_SUGGESTIONS
} from 'src/constants/search-page-display';
import { buildOptionList } from 'src/helpers';

const NoSearchResultsTab = props => {
  const {
    i18n,
    loading,
    searchTipsActive,
    searchTermSuggestionsActive,
    searchTermSuggestionsList,
    searchTermSuggestionsType,
    mostPopularProductsActive,
    mostPopularProductsType,
    mostPopularProductsList,
    swapOptionValues,
    resetField
  } = props;

  const [searchTermSuggestionsChoices, setSearchTermSuggestionsChoices] = useState([]);
  const [mostPopularProductsChoices, setMostPopularProductsChoices] = useState([]);

  useEffect(() => {
    const searchTermSuggestionsValues = buildOptionList(SEARCH_TERM_LIST, i18n);
    const mostPopularProductsValues = buildOptionList(PRODUCT_LIST, i18n);
    setSearchTermSuggestionsChoices(searchTermSuggestionsValues);
    setMostPopularProductsChoices(mostPopularProductsValues);
  }, []);

  return loading ? (
    <FormLayout>
      <SkeletonBodyText lines={1} />
      <SkeletonDisplayText size="small" />
      <SkeletonBodyText lines={1} />
      <SkeletonBodyText lines={1} />
      <SkeletonBodyText lines={1} />
      <SkeletonBodyText lines={1} />
    </FormLayout>
  ) : (
    <div className="no-search-results-tab">
      <FormLayout>
        <div>
          <Field
            name="searchEmptyResultMessages.label"
            component={RenderTextField}
            anotherProps={{ multiline: 2 }}
            label={i18n.translate(
              'AdminHTML.SearchPageDisplay.NoSearchResultsPage.displayText_Input_Label'
            )}
            placeholder={i18n.translate(
              'AdminHTML.SearchPageDisplay.Constants.searchPanelBlock_NoResultMessage',
              { terms: '{{ terms }}' }
            )}
          />
        </div>
        <Field
          name="searchTips.active"
          component={RenderToggleSwitch}
          label={i18n.translate(
            'AdminHTML.SearchPageDisplay.NoSearchResultsPage.searchTip_Switch_Label'
          )}
          onChange={e => {
            const checked = _.get(e, 'target.checked', false);
            if (!checked) {
              resetField('searchTips');
            }
          }}
        />
        {searchTipsActive && (
          <div className="groupFields">
            <Field
              name="searchTips.label"
              component={RenderTextField}
              label={i18n.translate(
                'AdminHTML.SearchPageDisplay.NoSearchResultsPage.searchTip_Input1_Title'
              )}
              placeholder={i18n.translate(
                'AdminHTML.SearchPageDisplay.Constants.searchPanelBlock_SearchTips'
              )}
            />
            <Field
              name="searchTips.searchTips"
              component={RenderTextField}
              anotherProps={{ multiline: 4 }}
              label={i18n.translate(
                'AdminHTML.SearchPageDisplay.NoSearchResultsPage.searchTip_Input2_Title'
              )}
              placeholder={i18n.translate(
                'AdminHTML.SearchPageDisplay.Constants.searchPanelBlock_SearchTips_Message'
              )}
            />
          </div>
        )}
        <Field
          name="searchTermSuggestions.active"
          component={RenderToggleSwitch}
          label={i18n.translate(
            'AdminHTML.SearchPageDisplay.NoSearchResultsPage.searchTerm_Switch_Label'
          )}
          onChange={e => {
            const checked = _.get(e, 'target.checked', false);
            if (!checked) {
              resetField('searchTermSuggestions');
            }
          }}
        />
        {searchTermSuggestionsActive && (
          <div className="groupFields">
            <FormLayout>
              <Field
                name="searchTermSuggestions.label"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.NoSearchResultsPage.searchTerm_Input1_Label'
                )}
                placeholder={i18n.translate(
                  'AdminHTML.SearchPageDisplay.Constants.searchPanelBlock_SearchTermSuggestions'
                )}
              />
              <div>
                <p style={{ marginBottom: '0.25rem' }}>
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.NoSearchResultsPage.searchTerm_Input2_Label'
                  )}
                </p>
                <div className="radioButtonGroup">
                  <Field
                    name="searchTermSuggestions.type"
                    component={RenderChoice2}
                    choices={searchTermSuggestionsChoices}
                    onChange={() => swapOptionValues('searchTerm')}
                  />
                  {searchTermSuggestionsType === 'manually' && (
                    <div>
                      <FieldArray
                        name="searchTermSuggestions.searchTermList"
                        component={SearchTermTable}
                        searchTermSuggestionsList={searchTermSuggestionsList}
                      />
                    </div>
                  )}
                </div>
              </div>
              {searchTermSuggestionsType === 'manually' &&
              searchTermSuggestionsList.length === NUMBER_SEARCH_TERM_SUGGESTIONS ? (
                <p>
                  <TextStyle variation="negative">
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Notice'
                      )}
                    </TextStyle>
                  </TextStyle>{' '}
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Paragraph2',
                    {
                      t: (
                        <TextStyle variation="strong">
                          {i18n.translate(
                            'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Paragraph2_t',
                            {
                              nt_number: NUMBER_SEARCH_TERM_SUGGESTIONS
                            }
                          )}
                        </TextStyle>
                      )
                    }
                  )}
                  <br />
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Paragraph3'
                  )}
                </p>
              ) : (
                ''
              )}
            </FormLayout>
          </div>
        )}
        <Field
          name="mostPopularProducts.active"
          component={RenderToggleSwitch}
          label={i18n.translate(
            'AdminHTML.SearchPageDisplay.NoSearchResultsPage.popularProduct_Switch_Label'
          )}
          onChange={e => {
            const checked = _.get(e, 'target.checked', false);
            if (!checked) {
              resetField('mostPopularProducts');
            }
          }}
        />
        {mostPopularProductsActive && (
          <div className="groupFields">
            <FormLayout>
              <Field
                name="mostPopularProducts.label"
                component={RenderTextField}
                label={i18n.translate(
                  'AdminHTML.SearchPageDisplay.NoSearchResultsPage.searchTerm_Input1_Label'
                )}
                placeholder={i18n.translate(
                  'AdminHTML.SearchPageDisplay.Constants.searchPanelBlock_PopularProducts'
                )}
              />
              <div>
                <p style={{ marginBottom: '0.25rem' }}>
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.NoSearchResultsPage.popularProduct_Input2_Label'
                  )}
                </p>
                <div className="radioButtonGroup">
                  <Field
                    name="mostPopularProducts.type"
                    component={RenderChoice2}
                    choices={mostPopularProductsChoices}
                    onChange={() => swapOptionValues('product')}
                  />
                  {mostPopularProductsType === 'manually' && (
                    <div>
                      <FieldArray
                        name="mostPopularProducts.productList"
                        component={ProductTable}
                        productSuggestionsList={mostPopularProductsList}
                        fieldName="mostPopularProducts.productList"
                      />
                    </div>
                  )}
                </div>
              </div>
              {mostPopularProductsType === 'manually' &&
              mostPopularProductsList.length === NUMBER_PRODUCT_SUGGESTIONS ? (
                <p>
                  <TextStyle variation="negative">
                    <TextStyle variation="strong">
                      {i18n.translate(
                        'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Notice'
                      )}
                    </TextStyle>
                  </TextStyle>{' '}
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Paragraph4',
                    {
                      t: (
                        <TextStyle variation="strong">
                          {i18n.translate(
                            'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Paragraph4_t',
                            {
                              nt_number: NUMBER_PRODUCT_SUGGESTIONS
                            }
                          )}
                        </TextStyle>
                      )
                    }
                  )}
                  <br />
                  {i18n.translate(
                    'AdminHTML.SearchPageDisplay.NoSearchResultsPage.noSearchResultSuggestions_Paragraph5'
                  )}
                </p>
              ) : (
                ''
              )}
            </FormLayout>
          </div>
        )}
      </FormLayout>
    </div>
  );
};
export default withI18n()(NoSearchResultsTab);
